import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseApp } from '../firebase.js';

const functions = getFunctions(firebaseApp);

/**
 * Send a custom email using Brevo SMTP via Cloud Function
 *
 * @param emailData - Email configuration object
 * @returns Promise with send result
 *
 * @example
 * ```javascript
 * await sendEmail({
 *   to: 'user@example.com',
 *   subject: 'Welcome!',
 *   html: '<h1>Welcome to VetriaAI</h1>',
 *   text: 'Welcome to VetriaAI'
 * });
 * ```
 */
export const sendEmail = async (emailData) => {
  try {
    const sendEmailFunction = httpsCallable(
      functions,
      'sendEmail'
    );

    const result = await sendEmailFunction(emailData);
    return result.data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw new Error(error.message || 'Failed to send email');
  }
};

/**
 * Send a test completion notification to HR
 * This is called automatically by Firestore trigger, but can be manually invoked if needed
 */
export const sendTestCompletionNotification = async (
  hrEmail,
  candidateName,
  testTitle,
  testId,
  score
) => {
  return sendEmail({
    to: hrEmail,
    subject: `New Test Submission: ${testTitle}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #991b1b;">New Test Submission</h1>
        <p><strong>${candidateName}</strong> has completed the test: <strong>${testTitle}</strong></p>
        ${score !== undefined ? `<p><strong>Score:</strong> ${score}/100</p>` : ''}
        <a href="https://vetriaai.com/hr/test-results/${testId}"
           style="display: inline-block; background-color: #991b1b; color: white;
                  padding: 12px 24px; text-decoration: none; border-radius: 5px;
                  margin-top: 20px;">
          View Results
        </a>
      </div>
    `,
    text: `${candidateName} has completed ${testTitle}. View results in your dashboard.`,
  });
};

/**
 * Send a welcome email to new users
 * This is called automatically by Firestore trigger, but can be manually invoked if needed
 */
export const sendWelcomeEmailManual = async (
  userEmail,
  userName,
  userType
) => {
  const dashboardUrl = userType === 'jobseeker'
    ? 'https://vetriaai.com/job-seeker'
    : 'https://vetriaai.com/hr';

  return sendEmail({
    to: userEmail,
    subject: 'Welcome to VetriaAI!',
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #991b1b;">Welcome to VetriaAI, ${userName}!</h1>
        <p>Thank you for joining our recruitment platform.</p>
        ${userType === 'jobseeker' ? `
          <p>Here's what you can do next:</p>
          <ul>
            <li>Complete your profile</li>
            <li>Upload your resume</li>
            <li>Take skills verification tests</li>
            <li>Apply for jobs</li>
          </ul>
        ` : `
          <p>Here's what you can do next:</p>
          <ul>
            <li>Set up your company profile</li>
            <li>Post job openings</li>
            <li>Create custom tests</li>
            <li>Review candidate applications</li>
          </ul>
        `}
        <a href="${dashboardUrl}"
           style="display: inline-block; background-color: #991b1b; color: white;
                  padding: 12px 24px; text-decoration: none; border-radius: 5px;
                  margin-top: 20px;">
          Go to Dashboard
        </a>
        <p style="margin-top: 30px; color: #666; font-size: 12px;">
          If you have any questions, feel free to contact our support team.
        </p>
      </div>
    `,
    text: `Welcome to VetriaAI, ${userName}! Thank you for joining our recruitment platform.`,
  });
};

/**
 * Send a password reset email
 */
export const sendPasswordResetEmail = async (
  userEmail,
  resetLink
) => {
  return sendEmail({
    to: userEmail,
    subject: 'Reset Your VetriaAI Password',
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #991b1b;">Reset Your Password</h1>
        <p>We received a request to reset your password for your VetriaAI account.</p>
        <p>Click the button below to reset your password:</p>
        <a href="${resetLink}"
           style="display: inline-block; background-color: #991b1b; color: white;
                  padding: 12px 24px; text-decoration: none; border-radius: 5px;
                  margin-top: 20px;">
          Reset Password
        </a>
        <p style="margin-top: 30px; color: #666; font-size: 14px;">
          If you didn't request this, you can safely ignore this email.
          This link will expire in 1 hour.
        </p>
        <p style="color: #666; font-size: 12px;">
          If the button doesn't work, copy and paste this link into your browser:<br>
          ${resetLink}
        </p>
      </div>
    `,
    text: `Reset your password by visiting: ${resetLink}. This link expires in 1 hour.`,
  });
};

/**
 * Send interview scheduled notification
 */
export const sendInterviewScheduledEmail = async (
  candidateEmail,
  candidateName,
  interviewDate,
  interviewTime,
  companyName
) => {
  return sendEmail({
    to: candidateEmail,
    subject: `Interview Scheduled with ${companyName}`,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #991b1b;">Interview Scheduled!</h1>
        <p>Hi ${candidateName},</p>
        <p>Great news! Your interview has been scheduled with <strong>${companyName}</strong>.</p>
        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <p style="margin: 5px 0;"><strong>Date:</strong> ${interviewDate}</p>
          <p style="margin: 5px 0;"><strong>Time:</strong> ${interviewTime}</p>
          <p style="margin: 5px 0;"><strong>Company:</strong> ${companyName}</p>
        </div>
        <p>Please make sure to:</p>
        <ul>
          <li>Prepare your answers to common interview questions</li>
          <li>Review the job description</li>
          <li>Test your internet connection beforehand</li>
          <li>Join 5 minutes early</li>
        </ul>
        <p>Good luck!</p>
      </div>
    `,
    text: `Interview scheduled with ${companyName} on ${interviewDate} at ${interviewTime}.`,
  });
};

/**
 * Send application status update
 */
export const sendApplicationStatusEmail = async (
  candidateEmail,
  candidateName,
  jobTitle,
  status,
  companyName
) => {
  const statusMessages = {
    shortlisted: {
      subject: `You've been shortlisted for ${jobTitle}`,
      title: 'Congratulations! You\'ve Been Shortlisted',
      message: `We're pleased to inform you that you've been shortlisted for the position of <strong>${jobTitle}</strong> at <strong>${companyName}</strong>.`,
    },
    rejected: {
      subject: `Application Update: ${jobTitle}`,
      title: 'Application Update',
      message: `Thank you for your interest in the position of <strong>${jobTitle}</strong> at <strong>${companyName}</strong>. After careful consideration, we've decided to move forward with other candidates.`,
    },
    interview: {
      subject: `Interview Invitation: ${jobTitle}`,
      title: 'You\'re Invited for an Interview!',
      message: `Congratulations! We'd like to invite you for an interview for the position of <strong>${jobTitle}</strong> at <strong>${companyName}</strong>.`,
    },
  };

  const { subject, title, message } = statusMessages[status];

  return sendEmail({
    to: candidateEmail,
    subject,
    html: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #991b1b;">${title}</h1>
        <p>Hi ${candidateName},</p>
        <p>${message}</p>
        ${status !== 'rejected' ? `
          <a href="https://vetriaai.com/job-seeker"
             style="display: inline-block; background-color: #991b1b; color: white;
                    padding: 12px 24px; text-decoration: none; border-radius: 5px;
                    margin-top: 20px;">
            View Details
          </a>
        ` : `
          <p style="margin-top: 20px;">We encourage you to continue exploring other opportunities on our platform.</p>
        `}
        <p style="margin-top: 30px;">Best regards,<br>The ${companyName} Team</p>
      </div>
    `,
    text: message.replace(/<\/?strong>/g, ''),
  });
};

